import { BookOpen, MessageCircle, Settings, Wrench } from 'lucide-react';
import { DescriptionText, Surface, VerticalStack } from '@workspace/silk-ui';
import { useScope } from '@workspace/scope-context';

import { Button } from '@/components/ui/button';
import { useNavigation } from '@/context/NavigationContext';

export default function SupportPage() {
  const navigation = useNavigation();
  const { activeTenant } = useScope();

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6 md:px-6">
      <Surface className="border-border/60 p-6 md:p-8">
        <VerticalStack gap={4}>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.24em] text-muted-foreground/60">Support</p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-foreground">Support is moving to a tenant-native workflow</h1>
            <DescriptionText className="mt-3 max-w-3xl text-sm leading-6 text-foreground/80">
              Legacy ticket pages are retired. This support surface is the placeholder for the future tenant-native
              workflow, so for now it focuses on guidance, setup, and help content instead of ticket detail screens.
            </DescriptionText>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button type="button" onClick={() => navigation.navigate('/help/support')}>
              Open support guidance
            </Button>
            <Button type="button" variant="outline" onClick={() => navigation.navigate('/help')}>
              Browse help center
            </Button>
            <Button type="button" variant="outline" onClick={() => navigation.navigate('/setup')}>
              Continue setup
            </Button>
          </div>
        </VerticalStack>
      </Surface>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SupportActionCard
          title="Help center"
          description="Search portal articles, tenant guidance, and compatibility notes."
          actionLabel="Browse help"
          onAction={() => navigation.navigate('/help')}
          icon={BookOpen}
        />
        <SupportActionCard
          title="Setup"
          description="Finish tenant onboarding tasks before deeper workflows depend on them."
          actionLabel="Open setup"
          onAction={() => navigation.navigate('/setup')}
          icon={Wrench}
        />
        <SupportActionCard
          title="Settings"
          description="Adjust personal portal preferences without changing tenant-owned data."
          actionLabel="Open settings"
          onAction={() => navigation.navigate('/settings')}
          icon={Settings}
        />
        <SupportActionCard
          title="Portal home"
          description="Return to the tenant-first landing page and choose a different workspace."
          actionLabel="Go home"
          onAction={() => navigation.navigate('/dashboard')}
          icon={MessageCircle}
        />
      </section>

      <Surface className="border-border/60 p-6">
        <VerticalStack gap={3}>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">Current status</p>
          <p className="text-sm font-semibold text-foreground">
            {activeTenant?.name
              ? `The current support placeholder is scoped to ${activeTenant.name}.`
              : 'Select a tenant to align support guidance with the right workspace context.'}
          </p>
          <DescriptionText className="text-sm leading-6">
            When the new support service exists, this route will become the canonical tenant-scoped intake and case
            management surface. Until then, use help content and setup guidance to unblock the next step.
          </DescriptionText>
        </VerticalStack>
      </Surface>
    </div>
  );
}

function SupportActionCard({
  actionLabel,
  description,
  icon: Icon,
  onAction,
  title,
}: {
  actionLabel: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  onAction: () => void;
  title: string;
}) {
  return (
    <Surface className="border-border/60 p-5">
      <VerticalStack gap={3}>
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted/40">
          <Icon className="h-5 w-5 text-foreground/70" />
        </div>
        <div>
          <h2 className="text-sm font-black text-foreground">{title}</h2>
          <DescriptionText className="mt-1.5 text-xs leading-5">{description}</DescriptionText>
        </div>
        <div>
          <Button type="button" variant="outline" onClick={onAction}>
            {actionLabel}
          </Button>
        </div>
      </VerticalStack>
    </Surface>
  );
}
